import React, { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash2, Loader2, Receipt, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatCurrency, formatDate, formatPeriod, periodOptions } from "@/lib/format";

/**
 * PaymentTxnModal — record partial / early / multiple payments and allocate to a period.
 * Props:
 *  - payment: current rent-roll row { id, tenant_id, tenant_name, period, gross, amount_paid }
 *  - onClose(), onSaved()
 */
export default function PaymentTxnModal({ payment, onClose, onSaved }) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [dateReceived, setDateReceived] = useState(new Date().toISOString().slice(0, 10));
  const [allocatedPeriod, setAllocatedPeriod] = useState(payment.period);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/transactions?tenant_id=${payment.tenant_id}`);
      setTxns(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [payment.tenant_id]);

  const gross = Number(payment.gross || 0);
  const paidForThisPeriod = useMemo(
    () => txns.filter((t) => t.allocated_period === payment.period).reduce((s, t) => s + Number(t.amount || 0), 0),
    [txns, payment.period]
  );
  const remaining = Math.max(0, gross - paidForThisPeriod);

  const save = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      await api.post("/transactions", {
        tenant_id: payment.tenant_id,
        amount: Number(amount),
        date_received: dateReceived,
        allocated_period: allocatedPeriod,
        notes,
      });
      toast.success(`${formatCurrency(amount)} allocated to ${formatPeriod(allocatedPeriod)}`);
      setAmount(""); setNotes("");
      await load();
      onSaved?.();
    } catch { toast.error("Could not record transaction"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this transaction?")) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Removed");
      await load();
      onSaved?.();
    } catch { toast.error("Could not remove"); }
  };

  const grouped = useMemo(() => {
    const g = new Map();
    for (const t of txns) {
      const arr = g.get(t.allocated_period) || [];
      arr.push(t);
      g.set(t.allocated_period, arr);
    }
    return Array.from(g.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [txns]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="payment-txn-modal">
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-2xl fade-up max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center">
              <Receipt className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg">Record payment</h3>
              <p className="text-xs text-slate-500">{payment.tenant_name} · {formatPeriod(payment.period)}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Summary strip */}
          <div className="grid grid-cols-3 gap-2 rounded-lg border border-slate-200">
            <Cell label="Rent due" value={formatCurrency(gross)} />
            <Cell label="Paid for period" value={formatCurrency(paidForThisPeriod)} accent="emerald" />
            <Cell label="Remaining" value={formatCurrency(remaining)} accent={remaining > 0 ? "red" : "slate"} bold />
          </div>

          {/* New transaction form */}
          <form onSubmit={save} className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="payment-txn-form">
            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Amount (R)</span>
                <input type="number" step="0.01" min="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="txn-amount-input" className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Date received</span>
                <input type="date" required value={dateReceived} onChange={(e) => setDateReceived(e.target.value)} data-testid="txn-date-input" className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Allocate to month</span>
                <select value={allocatedPeriod} onChange={(e) => setAllocatedPeriod(e.target.value)} data-testid="txn-period-select" className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
                  {periodOptions(6, 12).map((p) => <option key={p} value={p}>{formatPeriod(p)}{p === payment.period ? " (current)" : ""}</option>)}
                </select>
              </label>
            </div>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Notes</span>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="txn-notes-input" placeholder="EFT ref, cash, part-payment…" className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm" />
            </label>
            <div className="flex justify-end">
              <button type="submit" disabled={busy} data-testid="txn-save-btn" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add payment
              </button>
            </div>
          </form>

          {/* Existing transactions grouped by period */}
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Payment history</div>
            {loading ? (
              <div className="text-sm text-slate-500 py-3">Loading…</div>
            ) : txns.length === 0 ? (
              <div className="text-sm text-slate-500 py-3">No payments recorded yet.</div>
            ) : (
              <div className="space-y-3">
                {grouped.map(([period, list]) => {
                  const sum = list.reduce((s, t) => s + Number(t.amount || 0), 0);
                  const isCurrent = period === payment.period;
                  return (
                    <div key={period} className={`rounded-lg border ${isCurrent ? "border-slate-900" : "border-slate-200"}`}>
                      <div className={`flex items-center justify-between px-4 py-2.5 ${isCurrent ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-600"}`}>
                        <div className="text-xs font-semibold uppercase tracking-widest">{formatPeriod(period)}{isCurrent && " · current"}</div>
                        <div className="font-mono-plex text-sm font-semibold">{formatCurrency(sum)}</div>
                      </div>
                      <ul className="divide-y divide-slate-100">
                        {list.map((t) => (
                          <li key={t.id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`txn-row-${t.id}`}>
                            <div className="min-w-0">
                              <div className="font-medium text-slate-900 font-mono-plex">{formatDate(t.date_received)}</div>
                              {t.notes && <div className="text-xs text-slate-500 mt-0.5 truncate">{t.notes}</div>}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-mono-plex text-slate-900">{formatCurrency(t.amount)}</span>
                              <button onClick={() => del(t.id)} data-testid={`txn-delete-${t.id}`} className="text-slate-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end">
          <button onClick={onClose} className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> Done
          </button>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, bold, accent }) {
  const color = accent === "emerald" ? "text-emerald-700" : accent === "red" ? "text-red-700" : "text-slate-900";
  return (
    <div className="px-4 py-3 border-r border-slate-200 last:border-r-0">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`${bold ? "font-display text-lg font-semibold" : "text-sm font-mono-plex"} ${color} mt-1`}>{value}</div>
    </div>
  );
}
